// "Ehita oma komplekt" page (sections/bundle-builder.liquid).
//
// The shopper picks a quantity per colour (silicone and chrome mixed); this file keeps the summary
// (progress towards the free pair, price) in step and adds the whole mix to the cart in ONE
// request. The free pair itself comes from the "Osta 2, saa 1 tasuta" automatic discount in
// Shopify, so the cart and checkout always have the final say. The maths here only previews it:
// one free pair per three pairs, same as the picker on the product page.
//
// Cart hand-over mirrors ProductFormComponent#processBatchAddToCart: announce the add with a
// CartLinesUpdateEvent, post cart/add.js, then fetch the cart and the cart drawer section at the
// same time and resolve the event, which opens and refreshes the drawer and updates the icon.
import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const FREE_EVERY = 3;

/** @param {HTMLElement} root */
function init(root) {
  if (root.dataset.bbReady) return;
  root.dataset.bbReady = 'true';

  const moneyFormat = root.dataset.moneyFormat || '{{amount}}';
  const currency = root.dataset.currency || 'EUR';
  const addUrl = root.dataset.cartAddUrl || '/cart/add.js';

  const cards = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-bb-card]')]);
  const summary = /** @type {HTMLElement} */ (root.querySelector('[data-bb-summary]'));
  const fill = /** @type {HTMLElement} */ (root.querySelector('[data-bb-fill]'));
  const dots = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-bb-dot]')]);
  const labels = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-bb-label]')]);
  const messageText = /** @type {HTMLElement} */ (root.querySelector('[data-bb-message-text]'));
  const lines = /** @type {HTMLElement} */ (root.querySelector('[data-bb-lines]'));
  const totals = /** @type {HTMLElement} */ (root.querySelector('[data-bb-totals]'));
  const savingRow = /** @type {HTMLElement} */ (root.querySelector('[data-bb-saving-row]'));
  const savingEl = /** @type {HTMLElement} */ (root.querySelector('[data-bb-saving]'));
  const compareEl = /** @type {HTMLElement} */ (root.querySelector('[data-bb-compare]'));
  const totalEl = /** @type {HTMLElement} */ (root.querySelector('[data-bb-total]'));
  const errorEl = /** @type {HTMLElement} */ (root.querySelector('[data-bb-error]'));
  const addButton = /** @type {HTMLButtonElement} */ (root.querySelector('[data-bb-add]'));
  const ctaText = /** @type {HTMLElement} */ (root.querySelector('[data-bb-cta-text]'));
  const ctaPill = /** @type {HTMLElement} */ (root.querySelector('[data-bb-cta-pill]'));
  const ctaCompare = /** @type {HTMLElement} */ (root.querySelector('[data-bb-cta-compare]'));
  const ctaNow = /** @type {HTMLElement} */ (root.querySelector('[data-bb-cta-now]'));

  /** @type {Map<HTMLElement, number>} */
  const quantities = new Map(cards.map((card) => [card, 0]));
  let adding = false;

  const money = (/** @type {number} */ cents) => formatMoney(cents, moneyFormat, currency);

  function totalQuantity() {
    let sum = 0;
    for (const qty of quantities.values()) sum += qty;
    return sum;
  }

  function render() {
    const total = totalQuantity();
    const freeCount = Math.floor(total / FREE_EVERY);

    // Per-card state
    for (const card of cards) {
      const qty = quantities.get(card) || 0;
      const qtyEl = card.querySelector('[data-bb-qty]');
      const badge = /** @type {HTMLElement | null} */ (card.querySelector('[data-bb-badge]'));
      const minus = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-bb-minus]'));
      if (qtyEl) qtyEl.textContent = String(qty);
      if (badge) {
        badge.textContent = String(qty);
        badge.hidden = qty === 0;
      }
      if (minus) minus.disabled = qty === 0;
      if (qty > 0) card.setAttribute('data-selected', '');
      else card.removeAttribute('data-selected');
    }

    // Progress like the product page picker: capped at three (it stays "done" however much is
    // added), the bar itself starts moving after the first pair (1 = 0%, 2 = the middle, 3 = full).
    // Milestones: 1 pair, 2 pairs = free shipping (the cart's threshold is 30 EUR, two pairs are
    // 38), 3 pairs = the free pair.
    const step = Math.min(total, FREE_EVERY);
    fill.style.width = `${step <= 1 ? 0 : ((step - 1) / (FREE_EVERY - 1)) * 100}%`;
    for (const dot of dots) {
      const dotStep = parseInt(dot.dataset.bbDot || '0', 10);
      dot.classList.toggle('bundle-builder__dot--reached', step >= dotStep);
    }
    for (const label of labels) {
      label.classList.toggle('is-reached', step >= parseInt(label.dataset.bbLabel || '0', 10));
    }

    let message;
    if (total === 0) message = null;
    else if (total === 1) message = 'Lisa 1 veel ja saad tasuta saatmise';
    else if (total === 2) message = 'Tasuta saatmine! Lisa 1 veel ja saa 1 paar tasuta';
    else message = `Vahva! Said ${freeCount === 1 ? '1 paari' : `${freeCount} paari`} tasuta + Tasuta saatmise`;
    if (message === null) {
      // Empty state: the offer itself, with the free pair in bold.
      const bold = document.createElement('strong');
      bold.textContent = '+ 1 TASUTA';
      messageText.replaceChildren('Osta 2 Saa ', bold);
    } else {
      messageText.textContent = message;
    }

    // Selected lines (desktop summary)
    lines.replaceChildren();
    for (const card of cards) {
      const qty = quantities.get(card) || 0;
      if (qty === 0) continue;
      const item = document.createElement('li');
      item.className = 'bundle-builder__line';
      const name = document.createElement('span');
      name.className = 'bundle-builder__line-name';
      name.textContent = `${qty} × ${card.dataset.name}`;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'bundle-builder__line-remove';
      remove.textContent = 'Eemalda';
      remove.addEventListener('click', () => {
        quantities.set(card, 0);
        render();
      });
      item.append(name, remove);
      lines.append(item);
    }
    lines.hidden = total === 0;

    // Prices: every pair has its own price, the cheapest ones are the free ones (as in Shopify's
    // buy-X-get-Y). The pairs are all the same price today, but sort anyway to be safe.
    const prices = [];
    for (const card of cards) {
      const qty = quantities.get(card) || 0;
      for (let i = 0; i < qty; i += 1) prices.push(parseInt(card.dataset.price || '0', 10) || 0);
    }
    prices.sort((a, b) => b - a);
    const compare = prices.reduce((sum, price) => sum + price, 0);
    const paid = prices.slice(0, prices.length - freeCount).reduce((sum, price) => sum + price, 0);
    totals.hidden = total === 0;
    totalEl.textContent = money(paid);
    compareEl.textContent = money(compare);
    compareEl.hidden = freeCount === 0;
    savingEl.textContent = money(compare - paid);
    savingRow.hidden = freeCount === 0;

    // Button: any number from one pair up can go in the cart. Like the product page button it
    // shows a price pill (old price struck through when a pair is free).
    ctaPill.hidden = adding || total === 0;
    ctaNow.textContent = money(paid);
    ctaCompare.textContent = money(compare);
    ctaCompare.hidden = freeCount === 0;
    if (adding) {
      ctaText.textContent = 'Lisan…';
      addButton.disabled = true;
    } else if (total === 0) {
      ctaText.textContent = 'Vali värvid';
      addButton.disabled = true;
    } else {
      ctaText.textContent = 'Lisa ostukorvi';
      addButton.disabled = false;
    }
    summary.dataset.total = String(total);
  }

  root.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const plus = target.closest('[data-bb-plus]');
    const minus = target.closest('[data-bb-minus]');
    if (!plus && !minus) return;
    const card = /** @type {HTMLElement | null} */ (target.closest('[data-bb-card]'));
    if (!card) return;
    errorEl.hidden = true;
    const current = quantities.get(card) || 0;
    if (plus && card.dataset.available !== 'false') quantities.set(card, current + 1);
    if (minus) quantities.set(card, Math.max(0, current - 1));
    render();
  });

  /** @returns {Promise<any>} */
  function fetchCart() {
    return fetch(`${root.dataset.cartUrl || '/cart'}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  addButton.addEventListener('click', async () => {
    const total = totalQuantity();
    if (adding || total === 0) return;

    const items = cards
      .map((card) => ({ variantId: String(card.dataset.variantId), quantity: quantities.get(card) || 0 }))
      .filter((item) => item.quantity > 0);

    adding = true;
    errorEl.hidden = true;
    render();

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    root.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.variantId, quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items.map((item) => ({ id: Number(item.variantId), quantity: item.quantity })) }),
      }).then((result) => result.json());

      if (response.status) {
        // e.g. not enough stock: nothing was added
        errorEl.textContent = response.description || response.message || 'Lisamine ei õnnestunud. Proovi uuesti.';
        errorEl.hidden = false;
        const failedCart = await fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(failedCart),
          detail: {
            didError: true,
            items: failedCart.items,
            source: 'bundle-builder',
            sourceId: 'bundle-builder',
            itemCount: total,
          },
        });
        return;
      }

      const sectionsPromise = sectionIds.length
        ? fetch(`${window.Shopify?.routes?.root || '/'}?sections=${sectionIds.join(',')}`, { credentials: 'same-origin' })
            .then((result) => (result.ok ? result.json() : undefined))
            .catch(() => undefined)
        : Promise.resolve(undefined);
      const [cart, sections] = await Promise.all([fetchCart(), sectionsPromise]);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bundle-builder',
          sourceId: 'bundle-builder',
          itemCount: total,
          sections,
          didError: false,
        },
      });

      for (const card of cards) quantities.set(card, 0);
    } catch (error) {
      console.error('[bundle-builder]', error);
      deferred.reject(error);
      errorEl.textContent = 'Midagi läks valesti. Proovi uuesti.';
      errorEl.hidden = false;
    } finally {
      adding = false;
      render();
    }
  });

  render();
}

const run = () => document.querySelectorAll('[data-bundle-builder]').forEach((root) => init(/** @type {HTMLElement} */ (root)));
run();
document.addEventListener('shopify:section:load', run);
